// packages/charts/src/datePart.ts
//
// How a block shows a DATE PART's value (plan docs/plans-pending/
// 2026-10-16-every-time-dimension-carries-its-date-parts.md, Round 8 S1).
//
// A date part (`Order date · month of year`) is an integer column: 1-12,
// 1-4, 1-53 or the year. The backend stamps which result columns are parts
// on the spec (`date_part_keys: {column: unit}`, never guessed from a key
// pattern), and the block's `date_part_label` picks the words:
//
//   |         | number | short | long      |
//   | month   | 10     | Oct   | October   |
//   | quarter | 4      | Q4    | Quarter 4 |
//   | week    | 42     | W42   | Week 42   |
//   | year    | 2026   | 2026  | 2026      |
//
// The Python twin is `apps/backend/services/semantics/date_part_rule.py::
// format_date_part`; both satisfy ONE golden,
// `apps/backend/tests/fixtures/date-part-label-cases.json`.
//
// A label is display only: a click, a filter and a series ref carry the raw
// integer, never the label.

import { MONTH_ABBR, MONTH_FULL } from "./table/formatDateValue";

export const DATE_PART_UNITS = ["year", "quarter", "month", "week"] as const;
export type DatePartUnit = (typeof DATE_PART_UNITS)[number];

export const DATE_PART_LABEL_STYLES = ["number", "short", "long"] as const;
export type DatePartLabelStyle = (typeof DATE_PART_LABEL_STYLES)[number];

/** The spec key a block's choice is stored under; absent = the default. */
export const DATE_PART_LABEL_KEY = "date_part_label";
/** The spec key the backend stamps: `{result column: unit}`. */
export const DATE_PART_KEYS_KEY = "date_part_keys";
export const DEFAULT_DATE_PART_LABEL: DatePartLabelStyle = "short";

/** The highest value of each closed unit; the year has none. */
const MAX: Record<Exclude<DatePartUnit, "year">, number> = { quarter: 4, month: 12, week: 53 };

export function isDatePartUnit(value: unknown): value is DatePartUnit {
  return typeof value === "string" && (DATE_PART_UNITS as readonly string[]).includes(value);
}

/** A stored style, or the default for anything else (absent, unknown). */
export function readDatePartLabel(value: unknown): DatePartLabelStyle {
  return typeof value === "string" && (DATE_PART_LABEL_STYLES as readonly string[]).includes(value)
    ? (value as DatePartLabelStyle)
    : DEFAULT_DATE_PART_LABEL;
}

/** A part value as a whole number: `10`, `"10"` and `"2026.0"` (a Postgres
 *  numeric that slipped through) all read; anything else is null. */
function wholeNumber(value: unknown): number | null {
  if (typeof value === "boolean" || value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : NaN;
  return Number.isFinite(n) && Number.isInteger(n) ? n : null;
}

/** One part value as text. A year is always its digits (never `2,026`); a
 *  value outside its unit's range is shown as its number, so a bad row is
 *  visible and never renamed; a value that is not a whole number is shown
 *  as it came. */
export function formatDatePart(unit: DatePartUnit, value: unknown, style: DatePartLabelStyle): string {
  const n = wholeNumber(value);
  if (n === null) return value === null || value === undefined ? "" : String(value);
  const number = String(n);
  if (style === "number" || unit === "year" || n < 1 || n > MAX[unit]) return number;
  if (unit === "month") return style === "short" ? MONTH_ABBR[n - 1] : MONTH_FULL[n - 1];
  if (unit === "quarter") return style === "short" ? `Q${number}` : `Quarter ${number}`;
  return style === "short" ? `W${number}` : `Week ${number}`;
}

/** The spec's stamped parts, `{result column: unit}`; a malformed entry is
 *  left out, so it can never rename a column. */
export function datePartKeysOf(spec: unknown): Record<string, DatePartUnit> {
  const raw =
    spec && typeof spec === "object" ? (spec as Record<string, unknown>)[DATE_PART_KEYS_KEY] : undefined;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, DatePartUnit> = {};
  for (const [key, unit] of Object.entries(raw as Record<string, unknown>)) {
    if (isDatePartUnit(unit)) out[key] = unit;
  }
  return out;
}

/** A labeller for one column of this spec: the column's part values in the
 *  block's style, or null when the column is not a part. */
export function datePartLabellerFor(
  spec: unknown,
  column: string | null | undefined,
): ((value: unknown) => string) | null {
  if (!column) return null;
  const unit = datePartKeysOf(spec)[column];
  if (!unit) return null;
  const style = readDatePartLabel(
    spec && typeof spec === "object" ? (spec as Record<string, unknown>)[DATE_PART_LABEL_KEY] : undefined,
  );
  return (value: unknown) => formatDatePart(unit, value, style);
}

/** A breakdown on a part draws one series per part VALUE (`y_keys` are
 *  "2024", "2025" after the chokepoint's pivot): their names go into the
 *  spec's `labels`, the one place every legend, tooltip and series name
 *  reads (`labels[key] ?? key`). A label the spec already has wins; the
 *  keys themselves stay raw, so colours (`d:<raw>`) and clicks are unmoved. */
export function withDatePartSeriesLabels<
  T extends { breakdown_key?: string | null; y_keys?: string[] | null; labels?: Record<string, string> | null },
>(spec: T): T {
  const label = datePartLabellerFor(spec, spec.breakdown_key ?? null);
  const keys = spec.y_keys ?? [];
  if (!label || keys.length === 0) return spec;
  const labels: Record<string, string> = { ...(spec.labels ?? {}) };
  let changed = false;
  for (const key of keys) {
    if (labels[key] !== undefined) continue;
    const text = label(key);
    if (text !== key) {
      labels[key] = text;
      changed = true;
    }
  }
  return changed ? { ...spec, labels } : spec;
}

/** Part values in time order: numbers ascending, anything that is not a
 *  whole number (a NULL's "Not set", "Other") after them in the order it
 *  came. Stable. */
export function compareDatePartValues(a: unknown, b: unknown): number {
  const x = wholeNumber(a);
  const y = wholeNumber(b);
  if (x !== null && y !== null) return x - y;
  if (x !== null) return -1;
  if (y !== null) return 1;
  return 0;
}
